// statistics_clinic_01a_practice_centre_spread.md was rewritten (outside this
// session) into 11 scenario-based "Mixed DIY Practice Cases" — each a real
// workplace request where the statistic isn't named, replacing the old
// "Learner Practice (1 per solved example)" tables and removing the Home
// Tests section entirely (the DIY practice now IS the home task). This
// config mirrors that file's items and data exactly; every answer below is
// computed directly from each table's own numbers.

export const STATS_CLINIC_01A_TRACKER_ID = 'statistics-clinic-01a-practice-bank';

export const STATS_CLINIC_01A_LENSES = [
  ['business_question', 'Business question'],
  ['statistic_family', 'Statistic family'],
  ['statistic_chosen', 'Statistic chosen'],
  ['why_statistic', 'Why this statistic'],
  ['calculation', 'Calculation'],
  ['result', 'Result'],
  ['meaning', 'Meaning'],
  ['decision', 'Decision'],
  ['caution', 'Caution'],
];

export const STATS_CLINIC_01A_ITEMS = [
  {
    id: 'DIY01',
    title: 'DIY Practice 01: Patient update',
    scenario: 'The clinic manager is updating the reception notice. Patients keep asking how long a walk-in appointment normally takes, but yesterday one patient waited much longer because the doctor was called into an emergency. The manager does not want the notice to exaggerate the normal wait just because of that one unusual case.',
    tables: [{
      columns: ['visit_id', 'wait_minutes'],
      rows: [['V001', '8'], ['V002', '9'], ['V003', '10'], ['V004', '11'], ['V005', '57']],
    }],
    answerGuide: {
      business_question: 'What is the normal wait time to put on the reception notice?',
      statistic_family: 'Centre.',
      statistic_chosen: 'Median.',
      why_statistic: 'The 57-minute emergency-related wait is a one-off that would inflate the mean.',
      calculation: 'Mean = (8+9+10+11+57)/5 = 95/5 = 19. Sorted: 8, 9, 10, 11, 57; median = 10.',
      result: '10 minutes.',
      meaning: 'A typical wait is about 10 minutes, not 19.',
      decision: 'Put around 10 minutes on the notice; do not let the one emergency-delay visit skew it.',
      caution: 'The mean (19) is still useful for overall clinic capacity planning, just not for what to tell an individual patient.',
    },
    ruleTerms: {
      business_question: ['wait time', 'notice'],
      statistic_family: ['centre'],
      statistic_chosen: ['median'],
      why_statistic: ['57', 'one-off', 'inflate'],
      calculation: ['95', '19', '10', 'median'],
      result: ['10'],
      meaning: ['10', 'not', '19'],
      decision: ['10 minutes', 'notice'],
      caution: ['19', 'capacity', 'individual'],
    },
    criticalTerms: { calculation: ['10'], result: ['10'] },
  },
  {
    id: 'DIY02',
    title: 'DIY Practice 02: Staffing queue',
    scenario: 'The customer service lead is planning next week\'s rota. There is budget for one extra person, and the team needs to decide which contact route is creating the most demand. The lead wants the recommendation to be based on the route customers used most in the recent ticket sample, not on personal opinion.',
    tables: [{
      columns: ['contact_id', 'route'],
      rows: [['C001', 'email'], ['C002', 'chat'], ['C003', 'phone'], ['C004', 'email'], ['C005', 'chat'], ['C006', 'email'], ['C007', 'phone'], ['C008', 'email'], ['C009', 'chat']],
    }],
    answerGuide: {
      business_question: 'Which contact route should get the extra staffing headcount?',
      statistic_family: 'Centre / frequency.',
      statistic_chosen: 'Mode.',
      why_statistic: 'Route is categorical, so the most frequent route shows highest demand.',
      calculation: 'email = 4 (C001, C004, C006, C008), chat = 3 (C002, C005, C009), phone = 2 (C003, C007).',
      result: 'Email is the mode.',
      meaning: 'Email generates the most contact volume in this sample.',
      decision: 'Put the extra person on the email queue.',
      caution: 'Small sample (9 tickets) and email only leads chat 4 to 3 — confirm over a longer period before locking in the rota.',
    },
    ruleTerms: {
      business_question: ['route', 'staffing'],
      statistic_family: ['centre', 'frequency'],
      statistic_chosen: ['mode'],
      why_statistic: ['categorical', 'frequent'],
      calculation: ['email', '4', 'chat', '3', 'phone', '2'],
      result: ['email', 'mode'],
      meaning: ['email', 'most'],
      decision: ['email', 'extra'],
      caution: ['9 tickets', 'close', 'longer period'],
    },
    criticalTerms: { calculation: ['email'], result: ['email'] },
  },
  {
    id: 'DIY03',
    title: 'DIY Practice 03: Delivery promise',
    scenario: 'The logistics manager is choosing a courier for time-sensitive customer orders. Both courier options can deliver, and one sometimes delivers very quickly, but the manager is more concerned about avoiding unpredictable delays. The recommendation should identify which courier is the safer routine choice.',
    tables: [{
      columns: ['courier', 'shipment_id', 'delivery_days'],
      rows: [
        ['FastGo', 'F001', '2'], ['FastGo', 'F002', '3'], ['FastGo', 'F003', '3'], ['FastGo', 'F004', '4'], ['FastGo', 'F005', '3'],
        ['RoadMax', 'R001', '1'], ['RoadMax', 'R002', '2'], ['RoadMax', 'R003', '3'], ['RoadMax', 'R004', '7'], ['RoadMax', 'R005', '12'],
      ],
    }],
    answerGuide: {
      business_question: 'Which courier is the safer routine choice for time-sensitive orders?',
      statistic_family: 'Spread.',
      statistic_chosen: 'Standard deviation, with range as a quick check.',
      why_statistic: 'The manager needs consistency, not just occasional speed.',
      calculation: 'FastGo: mean = 15/5 = 3, SD = sqrt(0.4) = 0.63 days. RoadMax: mean = 25/5 = 5, SD = sqrt(16.4) = 4.05 days.',
      result: 'FastGo SD is about 0.63 days; RoadMax SD is about 4.05 days.',
      meaning: 'FastGo stays close to 3 days every time; RoadMax swings from 1 to 12 days.',
      decision: 'Use FastGo as the routine choice for time-sensitive orders.',
      caution: "RoadMax's fastest case (1 day) beats FastGo's best, but its unpredictability makes it risky for a routine promise.",
    },
    ruleTerms: {
      business_question: ['courier', 'safer'],
      statistic_family: ['spread'],
      statistic_chosen: ['standard deviation'],
      why_statistic: ['consistency', 'not', 'occasional'],
      calculation: ['0.63', '4.05', 'fastgo', 'roadmax'],
      result: ['fastgo', '0.63', 'roadmax', '4.05'],
      meaning: ['fastgo', 'close', 'roadmax', 'swings'],
      decision: ['fastgo', 'routine'],
      caution: ['fastest', 'unpredictability'],
    },
    criticalTerms: { result: ['FastGo'] },
  },
  {
    id: 'DIY04',
    title: 'DIY Practice 04: Department summary',
    scenario: 'The HR manager is writing a pay review note for a small department. Most staff are junior analysts, but the department head is included in the same list and earns much more. The note needs a fair description of what a normal employee in that department earns.',
    tables: [{
      columns: ['employee_id', 'monthly_salary'],
      rows: [['E001', '2200'], ['E002', '2350'], ['E003', '2400'], ['E004', '2450'], ['E005', '7300']],
    }],
    answerGuide: {
      business_question: 'What does a normal employee in this department earn, for the pay review note?',
      statistic_family: 'Centre.',
      statistic_chosen: 'Median.',
      why_statistic: "The department head's 7300 salary would massively inflate the mean.",
      calculation: 'Mean = (2200+2350+2400+2450+7300)/5 = 16700/5 = 3340. Sorted: 2200, 2350, 2400, 2450, 7300; median = 2400.',
      result: '2400.',
      meaning: 'A typical (non-head) employee earns around 2400, not 3340.',
      decision: 'Report the median (2400) as the "normal employee" figure and note the head\'s salary separately.',
      caution: 'Do not use the mean to describe "typical" pay here — it overstates it because of one senior salary.',
    },
    ruleTerms: {
      business_question: ['normal employee', 'earn'],
      statistic_family: ['centre'],
      statistic_chosen: ['median'],
      why_statistic: ['head', '7300', 'inflate'],
      calculation: ['16700', '3340', '2400', 'median'],
      result: ['2400'],
      meaning: ['2400', 'not', '3340'],
      decision: ['median', '2400', 'separately'],
      caution: ['mean', 'overstate', 'senior'],
    },
    criticalTerms: { calculation: ['2400'], result: ['2400'] },
  },
  {
    id: 'DIY05',
    title: 'DIY Practice 05: Branch comparison',
    scenario: 'A café owner is comparing two branches before deciding where to send extra weekend staff. She does not want to know only which branch can have a high day; she needs to understand what each branch usually takes and how stable those takings are across the week.',
    tables: [{
      columns: ['branch', 'day', 'takings'],
      rows: [
        ['East', 'Mon', '410'], ['East', 'Tue', '425'], ['East', 'Wed', '415'], ['East', 'Thu', '430'], ['East', 'Fri', '420'],
        ['West', 'Mon', '200'], ['West', 'Tue', '300'], ['West', 'Wed', '450'], ['West', 'Thu', '500'], ['West', 'Fri', '650'],
      ],
    }],
    answerGuide: {
      business_question: 'What does each branch usually take, and how stable are those takings?',
      statistic_family: 'Centre and Spread.',
      statistic_chosen: 'Mean plus standard deviation.',
      why_statistic: 'Both branches could average the same, but the owner needs to know which is stable enough to plan staffing around.',
      calculation: 'East: mean = 2100/5 = 420, SD = sqrt(50) = 7.07. West: mean = 2100/5 = 420, SD = sqrt(24600) = 156.8.',
      result: 'Both branches average £420/day, but East (SD ≈ £7) is far more consistent than West (SD ≈ £157).',
      meaning: 'East delivers steady takings around £420 every day; West swings from £200 to £650.',
      decision: "Send extra weekend staff to West, since a busy day there could be understaffed given how unpredictable its takings are.",
      caution: 'Two branches can share the exact same average but have very different risk profiles — never report the mean alone.',
    },
    ruleTerms: {
      business_question: ['usually take', 'stable'],
      statistic_family: ['centre', 'spread'],
      statistic_chosen: ['mean', 'standard deviation'],
      why_statistic: ['same', 'stable', 'plan'],
      calculation: ['420', '7.07', '156.8'],
      result: ['420', 'east', 'consistent', 'west'],
      meaning: ['east', 'steady', 'west', 'swings'],
      decision: ['staff', 'west', 'unpredictable'],
      caution: ['same average', 'risk'],
    },
    criticalTerms: { calculation: ['420'] },
    fallbackMinLength: { statistic_family: 8 },
  },
  {
    id: 'DIY06',
    title: 'DIY Practice 06: Window display',
    scenario: 'The store manager has one front-window display spot for Monday morning. Several colours sold over the weekend, and the manager wants the display to reflect what customers were choosing most often, while still being honest about how close the contest was.',
    tables: [{
      columns: ['sale_id', 'colour'],
      rows: [['S001', 'black'], ['S002', 'white'], ['S003', 'black'], ['S004', 'blue'], ['S005', 'white'], ['S006', 'black'], ['S007', 'green'], ['S008', 'white'], ['S009', 'black'], ['S010', 'blue']],
    }],
    answerGuide: {
      business_question: 'Which colour should get the Monday window display?',
      statistic_family: 'Centre / frequency.',
      statistic_chosen: 'Mode.',
      why_statistic: 'Colour is categorical, so the most frequent colour answers the question.',
      calculation: 'black = 4 (S001, S003, S006, S009), white = 3 (S002, S005, S008), blue = 2 (S004, S010), green = 1 (S007).',
      result: 'Black is the mode.',
      meaning: 'Black was the most-purchased colour this weekend, but only narrowly ahead of white (4 vs 3).',
      decision: 'Feature black in the window, but consider a secondary spot for white since the contest was close.',
      caution: 'The contest was close (4 vs 3 of 10) — a couple more sales either way would change the winner, so do not overstate confidence.',
    },
    ruleTerms: {
      business_question: ['colour', 'display'],
      statistic_family: ['centre', 'frequency'],
      statistic_chosen: ['mode'],
      why_statistic: ['categorical', 'frequent'],
      calculation: ['black', '4', 'white', '3', 'blue', '2', 'green', '1'],
      result: ['black', 'mode'],
      meaning: ['black', 'narrowly', 'white'],
      decision: ['black', 'secondary', 'white'],
      caution: ['close', 'few more sales'],
    },
    criticalTerms: { calculation: ['black'], result: ['black'] },
  },
  {
    id: 'DIY07',
    title: 'DIY Practice 07: Invoice briefing',
    scenario: 'The finance manager is preparing a short note for a client meeting. She needs one fair number to describe a normal invoice in this account, but one invoice is much larger than the others because it includes a yearly licence. The note should not make the usual invoice size look bigger than it really is.',
    tables: [{
      columns: ['invoice_id', 'value'],
      rows: [['I001', '82'], ['I002', '88'], ['I003', '91'], ['I004', '95'], ['I005', '480']],
    }],
    answerGuide: {
      business_question: 'What is a normal invoice size for this account, for the client meeting note?',
      statistic_family: 'Centre.',
      statistic_chosen: 'Median.',
      why_statistic: 'The 480 licence invoice is a one-off that would inflate the mean.',
      calculation: 'Mean = (82+88+91+95+480)/5 = 836/5 = 167.2. Sorted: 82, 88, 91, 95, 480; median = 91.',
      result: '91.',
      meaning: 'A normal invoice here is about 91, not 167.2.',
      decision: 'Report the median (91) as the usual invoice size and mention the 480 licence invoice separately.',
      caution: 'Do not let the mean make the usual invoice size look bigger than it really is.',
    },
    ruleTerms: {
      business_question: ['normal invoice', 'client meeting'],
      statistic_family: ['centre'],
      statistic_chosen: ['median'],
      why_statistic: ['480', 'licence', 'inflate'],
      calculation: ['836', '167.2', '91', 'median'],
      result: ['91'],
      meaning: ['91', 'not', '167'],
      decision: ['median', '91', 'separately'],
      caution: ['mean', 'bigger'],
    },
    criticalTerms: { calculation: ['91'], result: ['91'] },
  },
  {
    id: 'DIY08',
    title: 'DIY Practice 08: Operations risk',
    scenario: 'The operations lead is reviewing daily complaint pressure across two branches. She can see that both branches had complaint activity, but the staffing decision depends on which branch is more predictable day to day. A branch with sudden swings is harder to plan for than one with steady complaint levels.',
    tables: [{
      columns: ['branch', 'day', 'complaints'],
      rows: [
        ['A', 'Mon', '3'], ['A', 'Tue', '4'], ['A', 'Wed', '4'], ['A', 'Thu', '5'], ['A', 'Fri', '4'],
        ['B', 'Mon', '0'], ['B', 'Tue', '1'], ['B', 'Wed', '4'], ['B', 'Thu', '8'], ['B', 'Fri', '12'],
      ],
    }],
    answerGuide: {
      business_question: 'Which branch has more predictable day-to-day complaint levels, for the staffing decision?',
      statistic_family: 'Spread.',
      statistic_chosen: 'Standard deviation, with range as a quick check.',
      why_statistic: 'Predictability, not total volume, is what staffing planning needs.',
      calculation: 'Branch A: mean = 20/5 = 4, SD = sqrt(0.4) = 0.63. Branch B: mean = 25/5 = 5, SD = sqrt(20) = 4.47.',
      result: 'Branch A is far more predictable (SD ≈ 0.63 vs SD ≈ 4.47).',
      meaning: "A's complaints stay close to 4/day; B swings from 0 to 12.",
      decision: 'Plan steady staffing for A; build flexible or reactive capacity for B since its complaint pressure is volatile.',
      caution: "B's average (5) is not much higher than A's (4) — the real risk is B's volatility, not its average level.",
    },
    ruleTerms: {
      business_question: ['predictable', 'staffing'],
      statistic_family: ['spread'],
      statistic_chosen: ['standard deviation'],
      why_statistic: ['predictability', 'not', 'volume'],
      calculation: ['0.63', '4.47', 'branch a', 'branch b'],
      result: ['a', 'predictable', '0.63', '4.47'],
      meaning: ['a', 'close', 'b', 'swings'],
      decision: ['steady', 'a', 'flexible', 'b'],
      caution: ['average', 'volatility'],
    },
    criticalTerms: {},
  },
  {
    id: 'DIY09',
    title: 'DIY Practice 09: Sales team review',
    scenario: 'The sales director is reviewing two teams before assigning a key account. One team appears steady every week, while the other has some high weeks and some weak weeks. The director needs to know which team has stronger normal performance and which team is more reliable.',
    tables: [{
      columns: ['team', 'week', 'sales'],
      rows: [
        ['North', 'W1', '11800'], ['North', 'W2', '12100'], ['North', 'W3', '11950'], ['North', 'W4', '12250'], ['North', 'W5', '12000'],
        ['South', 'W1', '8500'], ['South', 'W2', '11000'], ['South', 'W3', '12500'], ['South', 'W4', '15000'], ['South', 'W5', '18800'],
      ],
    }],
    answerGuide: {
      business_question: 'Which team has stronger normal performance, and which is more reliable, for the key account assignment?',
      statistic_family: 'Centre and Spread.',
      statistic_chosen: 'Mean plus standard deviation.',
      why_statistic: 'A key account needs both a strong typical week and predictable service, not just a high average.',
      calculation: 'North: mean = 60100/5 = 12020, SD = sqrt(22600) ≈ 150.3. South: mean = 65800/5 = 13160, SD = sqrt(12402400) ≈ 3521.7.',
      result: "South's average is higher (13160 vs 12020), but North is far more consistent (SD ≈ 150 vs SD ≈ 3522).",
      meaning: 'North performs steadily around 12000/week; South swings wildly from 8500 to 18800.',
      decision: 'Assign the key account to North for reliability, even though South has occasional bigger weeks — a key account needs predictable service.',
      caution: "South's higher average could look more attractive at a glance, but it hides huge week-to-week risk.",
    },
    ruleTerms: {
      business_question: ['stronger', 'reliable', 'key account'],
      statistic_family: ['centre', 'spread'],
      statistic_chosen: ['mean', 'standard deviation'],
      why_statistic: ['typical', 'predictable'],
      calculation: ['12020', '150', '13160', '3521'],
      result: ['south', 'higher', 'north', 'consistent'],
      meaning: ['north', 'steadily', 'south', 'swings'],
      decision: ['north', 'reliability', 'key account'],
      caution: ['average', 'risk'],
    },
    criticalTerms: {},
  },
  {
    id: 'DIY10',
    title: 'DIY Practice 10: Product planning',
    scenario: 'The purchasing team is preparing next month\'s stock order. The sample is small, but they still need a first signal about which product category customers are choosing most often. The buyer wants the recommendation to include the count behind the chosen category.',
    tables: [{
      columns: ['order_id', 'category'],
      rows: [['O001', 'laptop'], ['O002', 'accessories'], ['O003', 'laptop'], ['O004', 'monitor'], ['O005', 'laptop'], ['O006', 'accessories']],
    }],
    answerGuide: {
      business_question: "Which product category should be prioritised in next month's stock order?",
      statistic_family: 'Centre / frequency.',
      statistic_chosen: 'Mode.',
      why_statistic: 'Category is categorical, so mean and median do not apply.',
      calculation: 'laptop = 3 (O001, O003, O005), accessories = 2 (O002, O006), monitor = 1 (O004).',
      result: 'Laptop is the mode, with 3 of 6 orders.',
      meaning: 'Laptop is the most-ordered category in this small sample.',
      decision: "Prioritise laptop stock for next month's order.",
      caution: 'Only 6 orders sampled — confirm with a larger extract before committing significant stock budget.',
    },
    ruleTerms: {
      business_question: ['category', 'prioritised'],
      statistic_family: ['centre', 'frequency'],
      statistic_chosen: ['mode'],
      why_statistic: ['categorical', 'not apply'],
      calculation: ['laptop', '3', 'accessories', '2', 'monitor', '1'],
      result: ['laptop', 'mode', '3'],
      meaning: ['laptop', 'most'],
      decision: ['laptop', 'stock'],
      caution: ['6 orders', 'larger'],
    },
    criticalTerms: { calculation: ['laptop'], result: ['laptop'] },
  },
  {
    id: 'DIY11',
    title: 'DIY Practice 11: Support rota',
    scenario: "The support manager is building next week's rota. One team has a very fast response in the sample, but also some much slower responses. The manager needs to know which team gives the more dependable response time, not just who had the fastest single response.",
    tables: [{
      columns: ['team', 'response_id', 'response_minutes'],
      rows: [
        ['Alpha', 'A001', '28'], ['Alpha', 'A002', '30'], ['Alpha', 'A003', '31'], ['Alpha', 'A004', '29'],
        ['Beta', 'B001', '12'], ['Beta', 'B002', '25'], ['Beta', 'B003', '42'], ['Beta', 'B004', '61'],
      ],
    }],
    answerGuide: {
      business_question: "Which team gives the more dependable response time, for next week's rota?",
      statistic_family: 'Spread.',
      statistic_chosen: 'Standard deviation, with range as a quick check.',
      why_statistic: 'One fast response does not prove dependability; the rota needs typical variation.',
      calculation: 'Alpha: mean = 118/4 = 29.5, SD = sqrt(1.25) = 1.12 minutes. Beta: mean = 140/4 = 35, SD = sqrt(338.5) = 18.4 minutes.',
      result: "Alpha is far more dependable (SD ≈ 1.12 vs SD ≈ 18.4); Beta's fastest response (12) beats Alpha's best, but Beta also has a 61-minute response.",
      meaning: 'Alpha stays close to 29-30 minutes; Beta ranges from 12 to 61 minutes.',
      decision: "Build the rota around Alpha's process for dependable coverage, and investigate what causes Beta's slow outliers.",
      caution: "Beta's single fastest response (12 min) should not be used to promise a response time — it is not representative.",
    },
    ruleTerms: {
      business_question: ['dependable', 'rota'],
      statistic_family: ['spread'],
      statistic_chosen: ['standard deviation'],
      why_statistic: ['fast response', 'dependability'],
      calculation: ['29.5', '1.12', '35', '18.4'],
      result: ['alpha', 'dependable', 'beta'],
      meaning: ['alpha', 'close', 'beta', 'ranges'],
      decision: ['alpha', 'rota', 'beta', 'outliers'],
      caution: ['fastest', 'not representative'],
    },
    criticalTerms: { result: ['Alpha'] },
  },
];

export const STATS_CLINIC_01A_CONFIG = {
  trackerId: STATS_CLINIC_01A_TRACKER_ID,
  resourceKey: 'statistics_clinic_01a_practice_centre_spread',
  rubricKey: 'statistics_centre_spread',
  activityScope: 'daily_concept_clinic_take_home',
  heading: 'Learner Practice: Savable Centre & Spread Cases',
  description: 'Each case is a business request. The statistic is deliberately not named in the title — read the scenario, identify what is really being asked, then show the maths. Your work autosaves to your own practice record.',
  introAlert: 'The final number is never enough — show how the statistic was chosen and how the result was derived before you write the decision.',
  lenses: STATS_CLINIC_01A_LENSES,
  workingField: null,
  items: STATS_CLINIC_01A_ITEMS,
};
